import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { evaluateFormula } from '../../../lib/formula';
import { cn } from '../../../lib/utils';
import { SpecraButton } from '../ensigns/button';
import { SpecraInput } from '../ensigns/input';
import { SpecraText } from '../ensigns/typography';
import { SpecraKeyValueRow } from '../lieutenants/key-value-row';
const fmtNumber = (v, maxDigits)=>v.toLocaleString('en-US', {
        maximumFractionDigits: maxDigits
    });
/** A value in its unit: "$1,234,567", "$12.3M", "12.5%", "1.2x", "1,880 h". */ export function formatScenarioValue(v, unit) {
    if (!Number.isFinite(v)) return '—';
    const u = (unit ?? '').trim();
    if (u === '$') {
        const sign = v < 0 ? '-' : '';
        const a = Math.abs(v);
        if (a >= 1e9) return `${sign}$${fmtNumber(a / 1e9, 2)}B`;
        if (a >= 1e7) return `${sign}$${fmtNumber(a / 1e6, 1)}M`;
        return `${sign}$${fmtNumber(a, a < 100 ? 2 : 0)}`;
    }
    if (u === '%') return `${fmtNumber(v, 1)}%`;
    if (u === 'x') return `${fmtNumber(v, 2)}x`;
    return u ? `${fmtNumber(v, 2)} ${u}` : fmtNumber(v, 2);
}
/** A slider's range and step when the reply gives none: 0 to twice the value. */ function sliderRange(input) {
    const v = input.value;
    const min = input.min ?? (v < 0 ? v * 2 : 0);
    let max = input.max ?? (v > 0 ? v * 2 : v < 0 ? 0 : 100);
    if (max <= min) max = min + 1;
    let step = input.step;
    if (!step || step <= 0) {
        const raw = (max - min) / 100;
        step = 10 ** Math.floor(Math.log10(raw));
        if (Number.isInteger(v) && step < 1 && max - min >= 10) step = 1;
    }
    return {
        min,
        max,
        step
    };
}
/** Sliders and live outputs for a what-if. */ export function SpecraScenario({ title, description, inputs, outputs, source, className, values: shared, onValuesChange }) {
    // Keyed by content, not identity: a parent re-render must not reset the sliders.
    const initialKey = JSON.stringify(inputs.map((i)=>[
            i.id,
            i.value
        ]));
    // biome-ignore lint/correctness/useExhaustiveDependencies: initialKey is the content of inputs
    const initial = React.useMemo(()=>Object.fromEntries(inputs.map((i)=>[
                i.id,
                i.value
            ])), [
        initialKey
    ]);
    const [own, setOwn] = React.useState(initial);
    React.useEffect(()=>setOwn(initial), [
        initial
    ]);
    const controlled = shared !== undefined && onValuesChange !== undefined;
    const values = controlled ? {
        ...initial,
        ...shared
    } : own;
    const setValues = (next)=>{
        const resolved = typeof next === 'function' ? next(values) : next;
        if (controlled) onValuesChange({
            ...shared,
            ...resolved
        });
        else setOwn(resolved);
    };
    const changed = inputs.some((i)=>values[i.id] !== i.value);
    const headingId = React.useId();
    const results = outputs.map((o)=>({
            output: o,
            value: evaluateFormula(o.formula, values)
        }));
    const primary = results.filter((r)=>r.output.primary);
    const rest = results.filter((r)=>!r.output.primary);
    return /*#__PURE__*/ _jsxs("section", {
        "aria-labelledby": title ? headingId : undefined,
        "aria-label": title ? undefined : 'Scenario',
        className: cn('rounded-md border border-border bg-card p-[var(--space-4)]', className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-[var(--space-3)]",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0",
                        children: [
                            title && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "font-medium",
                                id: headingId,
                                size: "body-sm",
                                children: title
                            }),
                            description && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: description
                            })
                        ]
                    }),
                    changed && /*#__PURE__*/ _jsx(SpecraButton, {
                        className: "shrink-0",
                        onClick: ()=>setValues(initial),
                        size: "inline",
                        type: "button",
                        variant: "link",
                        children: "Reset to the reply’s values"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn('mt-[var(--space-3)] grid gap-[var(--space-4)]', outputs.length > 0 && 'md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]'),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "grid content-start gap-[var(--space-3)]",
                        children: inputs.map((input)=>/*#__PURE__*/ _jsx(ScenarioSlider, {
                                input: input,
                                onChange: (v)=>setValues((prev)=>({
                                            ...prev,
                                            [input.id]: v
                                        })),
                                value: values[input.id] ?? input.value
                            }, input.id))
                    }),
                    outputs.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "grid content-start gap-[var(--space-3)] rounded-md bg-muted/40 p-[var(--space-4)]",
                        children: [
                            primary.length > 0 && /*#__PURE__*/ _jsx("div", {
                                "aria-live": "polite",
                                className: "grid gap-[var(--space-3)]",
                                children: primary.map(({ output, value })=>/*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                size: "caption",
                                                tone: "muted",
                                                children: output.label
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "font-display text-[length:var(--fs-ui-verdict)] font-medium leading-tight tabular-nums text-foreground transition-colors duration-[var(--dur-fast)]",
                                                children: formatScenarioValue(value, output.unit)
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "p",
                                                size: "caption",
                                                tone: "muted",
                                                children: Number.isFinite(value) ? output.note : 'Can’t compute with these inputs'
                                            })
                                        ]
                                    }, output.label))
                            }),
                            rest.length > 0 && /*#__PURE__*/ _jsx("div", {
                                "aria-live": "polite",
                                children: rest.map(({ output, value })=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        label: output.note ? `${output.label} · ${output.note}` : output.label,
                                        value: Number.isFinite(value) ? /*#__PURE__*/ _jsx("span", {
                                            className: "tabular-nums",
                                            children: formatScenarioValue(value, output.unit)
                                        }) : /*#__PURE__*/ _jsx("span", {
                                            title: "Can’t compute with these inputs",
                                            children: "—"
                                        })
                                    }, output.label))
                            })
                        ]
                    })
                ]
            }),
            source && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "mt-[var(--space-2)]",
                size: "caption",
                tone: "muted",
                children: [
                    "Source: ",
                    source
                ]
            })
        ]
    });
}
function ScenarioSlider({ input, value, onChange }) {
    const id = React.useId();
    const { min, max, step } = sliderRange(input);
    const [draft, setDraft] = React.useState(String(value));
    React.useEffect(()=>setDraft(String(value)), [
        value
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[var(--space-3)] gap-y-[var(--space-1)]",
        children: [
            /*#__PURE__*/ _jsxs("label", {
                className: "flex items-baseline justify-between gap-[var(--space-2)]",
                htmlFor: id,
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "caption",
                        children: input.label
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "tabular-nums",
                        size: "caption",
                        tone: "muted",
                        children: formatScenarioValue(value, input.unit)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("span", {}),
            /*#__PURE__*/ _jsx("input", {
                className: "h-[var(--space-4)] w-full cursor-pointer accent-brand",
                id: id,
                max: Math.max(max, value),
                min: Math.min(min, value),
                onChange: (e)=>onChange(Number(e.target.value)),
                step: step,
                type: "range",
                value: value
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "aria-label": `${input.label} value`,
                className: "w-[calc(var(--space-12)*2)] tabular-nums",
                inputMode: "decimal",
                onBlur: ()=>setDraft(String(value)),
                onChange: (e)=>{
                    setDraft(e.target.value);
                    const n = Number(e.target.value);
                    if (e.target.value.trim() !== '' && Number.isFinite(n)) onChange(n);
                },
                type: "number",
                value: draft
            })
        ]
    });
}
